import { Grid, TableRow, TextField } from "@mui/material";
import React from "react";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TablePagination from "@mui/material/TablePagination";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ListWrap from "../commonComponent/listWrap";
import { getFormattedDates } from "../../utils/utils";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { BASE_URL } from "../../global";
import moment from "moment";
import { parseRange } from "../../utils/components/accuracyandlcandrangesshow";
import ExcelDownload from "../../utils/components/excelDownload";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const notNeededColumn = ["id", "status", "type"];
const ApprovalStatusReport = () => {
  const [from, setFrom] = React.useState(null);
  const [to, setTo] = React.useState(null);
  const [searched, setSearched] = React.useState(false);
  const [data, setData] = React.useState([]);
  const [downloadPdfloading, setDownloadPdfloading] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const [page, setPage] = React.useState(0);
  const [roleVisibility, setRoleVisibility] = React.useState({
    activeRole1: true,
    activeRole2: true,
    activeRole3: true,
    activeRole4: true,
    activeRole5: true,
    companyHierarchy: false,
  });

  const _clientId = localStorage.getItem("clientId");
  const _userType = localStorage.getItem("type");
  const _companyHierarchy = localStorage.getItem("companyHierarchy");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getData = async () => {
    const url = BASE_URL;
    let whereConditions = "";

    if (to != null && from != null) {
      const { fromDate, toDate } = getFormattedDates(from, to);
      if (whereConditions === "") {
        whereConditions = " where ";
      } else {
        whereConditions += " and ";
      }
      whereConditions += ` calibrationDate between '${fromDate}' and '${toDate}'`;
    }

    if (whereConditions === "") {
      whereConditions += ` where calibratedby IS NOT NULL AND totalReadings IS NOT NULL AND totalReadings > 0 and status != -1`;
    } else {
      whereConditions += ` and calibratedby IS NOT NULL AND totalReadings IS NOT NULL AND totalReadings > 0 and status != -1`;
    }

    if (_userType == 3 && _companyHierarchy && _companyHierarchy !== "null") {
      const arr = _companyHierarchy.split(",");
      const mid = Math.floor(arr.length / 2);

      if (arr.length >= 2 && arr.length % 2 === 0) {
        const pairs = arr.slice(0, mid).map((prefix, i) => {
          const suffix = arr[mid + i];
          return `(companyHierarchy = '${prefix},${suffix}')`;
        });

        if (pairs?.length) {
          whereConditions += ` AND ${pairs.join(" OR ")}`;
        }
      }
    }
    if (_userType == 3) {
      whereConditions += ` AND clientId = ${_clientId}`;
    }

    const query = {
      query: `select * from certificatesView ${whereConditions} order by id DESC`,
    };

    setDownloadPdfloading(true);

    try {
      // Step 1: Fetch certificates
      const res = await axiosWithToken.post(url + `dynamic`, query);
      const allCertificates = res.data;

      if (!allCertificates?.length) {
        setData([]);
        setDownloadPdfloading(false);
        return;
      }

      // Step 2: Collect unique user IDs from all roles
      const userIds = new Set();
      allCertificates.forEach((item) => {
        [
          "calibratedby",
          "approvedby",
          "reviewedBy",
          "signedBy",
          "witnessBy",
        ].forEach((key) => {
          if (item[key]) userIds.add(item[key]);
        });
      });

      // Step 3: Fetch usernames from users table
      let userMap = {};
      if (userIds.size > 0) {
        const userQuery = {
          query: `select id, username from users where id in (${[
            ...userIds,
          ].join(",")})`,
        };
        const userRes = await axiosWithToken.post(url + `dynamic`, userQuery);
        const userData = userRes.data;

        // Create a map { id: username }
        userData.forEach((u) => {
          userMap[u.id] = u.username;
        });
      }

      // Step 4: Replace IDs with usernames
      const certificates = allCertificates.map((item) => {
        const [division = "-", department = "-"] = item.companyHierarchy
          ? item.companyHierarchy.split(",")
          : ["-", "-"];

        return {
          id: item.id,
          companyName: item.companyName,
          serviceReqNumber: item.serviceReqNumber || "-",
          division,
          department,
          instrumentName: item.requestedname || item.instrumentName,
          DUCID: item.DUCID,
          lc: parseRange(item.lc),
          ranges: parseRange(item.ranges),
          serialNumber: item.serialNumber,
          locationOfInstrument: item.locationOfInstrument,
          calibrationDate: item.calibrationDate ? moment(item.calibrationDate).format("DD/MM/YYYY") : "",
          nextDueDate: item.nextDueDate ? moment(item.nextDueDate).format("DD/MM/YYYY") : "",
          calibratedby: userMap[item.calibratedby] || "-",
          approvedby: userMap[item.approvedby] || "-",
          reviewedBy: userMap[item.reviewedBy] || "-",
          signedBy: userMap[item.signedBy] || "-",
          witnessBy: userMap[item.witnessBy] || "-",
        };
      });

      setData(certificates);
    } catch (error) {
      console.error("Error fetching certificatesView:", error);
    } finally {
      setDownloadPdfloading(false);
    }
  };

  const search = () => {
    setSearched(true);
    getData();
  };

  const clearSearch = () => {
    setSearched(false);
    setFrom(null);
    setTo(null);
    setData([]);
  };

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosWithToken.post(BASE_URL + "dynamic", {
         query: `SELECT keyname, value FROM settings WHERE keyname IN ('activeRole1', 'activeRole2', 'activeRole3', 'activeRole4', 'activeRole5', 'companyHierarchy') and status = 1`,
        });
        const settings = res.data;

        // Convert to map: { activeRole1: true/false, ... }
        const roles = {};
        settings.forEach((s) => {
          roles[s.keyname] =
            s.value !== "false" && s.value !== "" && s.value !== null;
        });

        setRoleVisibility((prev) => ({ ...prev, ...roles }));
      } catch (err) {
        console.error("Error fetching settings:", err);
      }
    };

    fetchSettings();
  }, []);

  const downloadPDF = () => {
    if (!data.length) {
      alert("No data available to download!");
      return;
    }
    let config = {
      margin: 12,
      borderMagin: 7,
      headerHeight: 60,
      footerHeight: 60,
      fontSize: 6.7,
      bordergap: 5,
      currentY: 0,
    };

    const doc = new jsPDF({ orientation: "landscape" });
    const tableColumn = ["Sr.No.", "Client Name", "Service Req. No."];

    // 👇 Add dynamic columns conditionally
    if (roleVisibility.companyHierarchy) {
      tableColumn.push("Division", "Department");
    }

    tableColumn.push(
      "Instrument Name / Requested",
      "Range",
      "LC",
      "DUCID",
      "Serial Number",
      "DUC Location",
      "Calibration Date",
      "Due Date"
    );

    if (roleVisibility.activeRole1) tableColumn.push("Calibrated By");
    if (roleVisibility.activeRole2) tableColumn.push("Approved By");
    if (roleVisibility.activeRole3) tableColumn.push("Reviewed By");
    if (roleVisibility.activeRole4) tableColumn.push("Signed By");
    if (roleVisibility.activeRole5) tableColumn.push("Witness By");

    const tableRows = [];

    data.forEach((row, index) => {
      const rowData = [
        index + 1,
        row.companyName || "-",
        row.serviceReqNumber || "-",
      ];

      if (roleVisibility.companyHierarchy) {
        rowData.push(row.division || "-", row.department || "-");
      }

      rowData.push(
        row.instrumentName || "-",
        row.ranges || "-",
        row.lc || "-",
        row.DUCID || "-",
        row.serialNumber || "-",
        row.locationOfInstrument || "-",
        row.calibrationDate,
        row.nextDueDate,
      );

      if (roleVisibility.activeRole1) rowData.push(row.calibratedby || "-");
      if (roleVisibility.activeRole2) rowData.push(row.approvedby || "-");
      if (roleVisibility.activeRole3) rowData.push(row.reviewedBy || "-");
      if (roleVisibility.activeRole4) rowData.push(row.signedBy || "-");
      if (roleVisibility.activeRole5) rowData.push(row.witnessBy || "-");

      tableRows.push(rowData);
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const title = "Approval Status Report";
    doc.setFontSize(14);
    // Calculate text width and center X position
    const textWidth = doc.getTextWidth(title);
    const x = (pageWidth - textWidth) / 2;
    // Draw text centered at top (Y = 15)
    doc.text(title, x, 15);


    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      startY: 25,
      styles: {
        fontSize: config.fontSize,
        halign: "center",
        valign: "middle",
        textColor: 0,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        cellPadding: 1.4,
      },
      headStyles: { fillColor: false },
      margin: { left: config.margin, right: config.margin, bottom: config.margin + 10 },
    });

    doc.save("Approval_Status_Report.pdf");
  };

  return (
    <ListWrap>
      <Grid
        container
        spacing={1}
        justifyContent={"start"}
        alignItems={"center"}
      >
        {" "}
        <Grid item xs={6} sm={3} md={2} lg={2} textAlign={"left"}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              className="Listinputcss"
              label="From"
              value={from ? new Date(from) : null}
              inputFormat="dd/MM/yyyy"
              format="dd/MM/yyyy"
              onChange={(newValue) => {
                setFrom(newValue);
                setSearched(false);
              }}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6} sm={3} md={2} lg={2} textAlign={"left"}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              slotProps={{ textField: { size: "small", fullWidth: true } }}
              className="Listinputcss"
              label="To"
              value={to ? new Date(to) : null}
              inputFormat="dd/MM/yyyy"
              format="dd/MM/yyyy"
              onChange={(newValue) => {
                setTo(newValue);
                setSearched(false);
              }}
              renderInput={(params) => <TextField {...params} size="small" />}
              style={{ marginRight: "20px" }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={3} sm={1} md={1} lg={1} textAlign={"left"}>
          <Button
            variant="contained"
            size="small"
            className="Listinputcss"
            color={searched ? "error" : "primary"}
            sx={{ m: 0 }}
            disabled={!(from && to)}
            onClick={() => {
              searched ? clearSearch() : search();
            }}
          >
            {searched ? <RestartAltIcon /> : <SearchIcon />}
          </Button>
        </Grid>
        <Grid item xs={2} sm={2} md={2} lg={2} textAlign={"left"}>
          <ExcelDownload
            finalData={data.map((row) => {
              // Build a dynamic object based on visible roles and hierarchy setting
              const filteredRow = {
                "Client Name": row.companyName,
                "Service Req. No." : row.serviceReqNumber,
              };
              //Add Division & Department immediately after Client Name
              if (roleVisibility.companyHierarchy) {
                filteredRow["Division"] = row.division || "-";
                filteredRow["Department"] = row.department || "-";
              }
              // Continue with other fields
              filteredRow["Instrument Name / Requested"] = row.instrumentName;
              filteredRow["Range"] = row.ranges;
              filteredRow["LC"] = row.lc;
              filteredRow["DUCID"] = row.DUCID;
              filteredRow["Serial Number"] = row.serialNumber;
              filteredRow["DUC Location"] = row.locationOfInstrument;
              filteredRow["Calibration Date"] = row.calibrationDate;
              filteredRow["Due Date"] = row.nextDueDate;

              if (roleVisibility.activeRole1)
                filteredRow["Calibrated By"] = row.calibratedby;
              if (roleVisibility.activeRole2)
                filteredRow["Approved By"] = row.approvedby;
              if (roleVisibility.activeRole3)
                filteredRow["Reviewed By"] = row.reviewedBy;
              if (roleVisibility.activeRole4)
                filteredRow["Signed By"] = row.signedBy;
              if (roleVisibility.activeRole5)
                filteredRow["Witness By"] = row.witnessBy;

              return filteredRow;
            })}
            notNeededColumn={notNeededColumn}
          />
        </Grid>
         <Grid item xs={6} sm={3} md={2} lg={2} textAlign={"left"}>
           <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={downloadPDF}
            disabled={!data.length}
          >
            Download PDF
          </Button>
        </Grid>
      </Grid>
      <br />

      <TableContainer>
        <div style={{ width: "100%", margin: "auto" }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            size="small"
            style={{ borderCollapse: "collapse", marginTop: "5px" }}
          >
            <TableRow>
              <TableCell><b>Sr.No.</b></TableCell>
              <TableCell><b>Client Name</b></TableCell>
              <TableCell><b>Service Req. No.</b></TableCell>
              {roleVisibility.companyHierarchy && (<>  <TableCell><b>Division</b></TableCell>  <TableCell><b>Department</b></TableCell></>
              )}
              <TableCell><b>Instrument Name / Requested</b></TableCell>
              <TableCell><b>Range</b></TableCell>
              <TableCell><b>LC</b></TableCell>
              <TableCell><b>DUCID</b></TableCell>
              <TableCell><b>Serial Number</b></TableCell>
              <TableCell><b>DUC Location</b></TableCell>
              <TableCell><b>Calibration Date</b></TableCell>
              <TableCell><b>Due Date</b></TableCell>
              {roleVisibility.activeRole1 && (<TableCell>  <b>Calibrated By</b></TableCell>)}
              {roleVisibility.activeRole2 && (<TableCell>  <b>Approved By</b></TableCell>)}
              {roleVisibility.activeRole3 && (<TableCell>  <b>Reviewed By</b></TableCell>)}
              {roleVisibility.activeRole4 && (<TableCell>  <b>Signed By</b></TableCell>)}
              {roleVisibility.activeRole5 && (<TableCell>  <b>Witness By</b></TableCell>)}
            </TableRow>
            <TableBody className="single-border-table">
              {data?.length > 0 ? (
                data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow
                      id="pagbreaksborder"
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                      style={{ pageBreakInside: "avoid" }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.companyName || "-"}</TableCell>
                      <TableCell>{row.serviceReqNumber || "-"}</TableCell>
                      {roleVisibility.companyHierarchy && (
                        <>
                          <TableCell>{row.division || "-"}</TableCell>
                          <TableCell>{row.department || "-"}</TableCell>
                        </>
                      )}
                      <TableCell>{row.instrumentName || "-"}</TableCell>
                      <TableCell>{row.ranges || "-"}</TableCell>
                      <TableCell>{row.lc || "-"}</TableCell>
                      <TableCell>{row.DUCID || "-"}</TableCell>
                      <TableCell>{row.serialNumber || "-"}</TableCell>
                      <TableCell>{row.locationOfInstrument || "-"}</TableCell>
                      <TableCell>{row.calibrationDate}</TableCell>
                      <TableCell>{row.nextDueDate}</TableCell>
                      {roleVisibility.activeRole1 && (<TableCell>{row.calibratedby || "-"}</TableCell>)}
                      {roleVisibility.activeRole2 && (<TableCell>{row.approvedby || "-"}</TableCell>)}
                      {roleVisibility.activeRole3 && (<TableCell>{row.reviewedBy || "-"}</TableCell>)}
                      {roleVisibility.activeRole4 && (<TableCell>{row.signedBy || "-"}</TableCell>)}
                      {roleVisibility.activeRole5 && (<TableCell>{row.witnessBy || "-"}</TableCell>)}
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <h6>
                      <b>Data Not Found</b>
                    </h6>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>{" "}
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[10, 30, 50, 100]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </ListWrap>
  );
};

export default ApprovalStatusReport;
